import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Dropdown } from "antd";
import { MoreOutlined, PlusOutlined } from "@ant-design/icons";
import CommonImages from "@base/assets/images";
import CommonStyles from "@components/CommonStyles";
import useToggle from "@base/hooks/useToggle";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import { ILifeInsurance } from "@base/modules/patientInsurance/lifeInsurance.interface";
import patientInsuranceService from "@base/modules/patientInsurance/patientInsuranceService";
import LifeInsuranceItem from "@components/User/Profile/InsuranceCards/LifeInsuranceItem";
import InsuranceDetailModal from "@components/User/Profile/InsuranceCards/InsuranceDetailModal";
import LifeInsuranceForm from "./LifeInsuranceForm";

interface IProps {
  patientRecord: IPatientRecord;
}

/** The record's health insurances, with add, and edit/delete where the record allows (`can_edit`). */
export default function LifeInsuranceSection({ patientRecord }: IProps) {
  //! state
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const patientRecordCode = String(patientRecord.code ?? "");
  const { open: openForm, shouldRender: shouldRenderForm, toggle: toggleForm } = useToggle();
  const { open: openDelete, shouldRender: shouldRenderDelete, toggle: toggleDelete } = useToggle();

  const [insurances, setInsurances] = useState<ILifeInsurance[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasFailed, setHasFailed] = useState<boolean>(false);
  const [viewing, setViewing] = useState<ILifeInsurance>();
  const [editing, setEditing] = useState<ILifeInsurance>();
  const [deleting, setDeleting] = useState<ILifeInsurance>();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  //! function
  const getInsurances = useCallback(async () => {
    if (!patientRecordCode) {
      return;
    }

    try {
      setIsLoading(true);
      setHasFailed(false);
      const resp = await patientInsuranceService.getLifeByPatientCode(patientRecordCode);
      setInsurances(resp.data ?? []);
    } catch (error) {
      // An empty list is shown rather than whatever the previous record had.
      setInsurances([]);
      setHasFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [patientRecordCode]);

  useEffect(() => {
    getInsurances();
  }, [getInsurances]);

  const openAdd = () => {
    setEditing(undefined);
    toggleForm();
  };

  const onDelete = async () => {
    if (!deleting?.id) {
      return;
    }

    try {
      setIsDeleting(true);
      await patientInsuranceService.deleteLife(deleting.id);
      dispatch(setAlertMessage({ type: "success", message: t("Notification"), description: t("Successfully") + "!" }));
      toggleDelete();
      getInsurances();
    } catch (error) {
      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: t("Failed") + "!" }));
    } finally {
      setIsDeleting(false);
    }
  };

  const describe = (insurance?: ILifeInsurance) =>
    insurance?.health_insurance ? `${insurance.health_insurance.provider} - ${insurance.health_insurance.name}` : (insurance?.name ?? "");

  //! render
  return (
    <div className="min-h-[400px] relative flex flex-col gap-4">
      <CommonStyles.SpinnerUI isSpinning={isLoading} />

      <div className="flex justify-between items-center gap-2">
        <span className="text-common-subtext text-sm font-normal font-['Inter']">{t("insurance.lifeDescription")}</span>

        <CommonStyles.ButtonUI
          icon={<PlusOutlined />}
          className="bg-common-primary200 text-common-primary hover:!bg-common-primary300 hover:!text-common-primary"
          onClick={openAdd}
        >
          {t("insurance.add")}
        </CommonStyles.ButtonUI>
      </div>

      {insurances.map((insurance) => (
        <LifeInsuranceItem
          key={insurance.id}
          insurance={insurance}
          patientRecord={patientRecord}
          onViewDetail={() => setViewing(insurance)}
          actions={
            // A record the insurer manages cannot be changed from here.
            insurance.can_edit ? (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    { key: "edit", label: t("insurance.edit") },
                    { key: "delete", label: <span className="text-common-red500">{t("Delete")}</span> },
                  ],
                  onClick: ({ key }) => {
                    if (key === "edit") {
                      setEditing(insurance);
                      toggleForm();
                      return;
                    }

                    setDeleting(insurance);
                    toggleDelete();
                  },
                }}
              >
                <CommonStyles.ButtonUI type="text" icon={<MoreOutlined />} />
              </Dropdown>
            ) : null
          }
        />
      ))}

      {!isLoading && !insurances.length ? (
        <div className="w-full min-h-[320px] flex flex-col justify-center items-center gap-6">
          <img src={CommonImages.EmptyRecordList} alt="" />
          <div className="text-common-maintext text-base font-semibold font-['Inter']">
            {hasFailed ? t("user.profile.insurance.loadFailed") : t("insurance.lifeEmpty")}
          </div>
          {hasFailed ? <CommonStyles.ButtonUI onClick={getInsurances}>{t("user.profile.insurance.retry")}</CommonStyles.ButtonUI> : null}
        </div>
      ) : null}

      <InsuranceDetailModal insurance={viewing} onClose={() => setViewing(undefined)} />

      {shouldRenderForm && (
        <LifeInsuranceForm
          open={openForm}
          toggle={toggleForm}
          patientRecordCode={patientRecordCode}
          insurance={editing}
          onSaved={() => {
            toggleForm();
            getInsurances();
          }}
        />
      )}

      {shouldRenderDelete && (
        <CommonStyles.ModalConfirmationUI
          open={openDelete}
          toggle={toggleDelete}
          title={t("insurance.confirmTitle")}
          content={<div className="whitespace-pre-line">{t("insurance.confirmDelete", { record: describe(deleting) })}</div>}
          okButtonProps={{ loading: isDeleting }}
          onConfirm={onDelete}
        />
      )}
    </div>
  );
}
